import { z } from 'zod';
import { PROJECT_ROLES, ROOM_KINDS } from './roles.js';
import { PROVIDERS } from './providers.js';
export const API_VERSION = 'v1';
const uuid = z.string().uuid();
const clientId = z
    .string()
    .min(8)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/);
export const LoginBody = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(512),
    client: z.enum(['web', 'mobile']).default('web'),
    deviceLabel: z.string().max(80).optional(),
});
export const AcceptInviteBody = z.object({
    token: z.string().min(20).max(200),
    email: z.string().email().max(254),
    displayName: z.string().min(1).max(80),
    password: z.string().min(12).max(512),
});
export const CreateInviteBody = z.object({
    email: z.string().email().max(254),
    projectId: uuid.optional(),
    role: z.enum(PROJECT_ROLES).default('member'),
    ttlHours: z
        .number()
        .int()
        .min(1)
        .max(24 * 14)
        .default(72),
});
export const CreateProjectBody = z.object({
    name: z.string().min(1).max(120),
});
export const AddMemberBody = z.object({
    userId: uuid,
    role: z.enum(PROJECT_ROLES),
});
export const CreateRoomBody = z.object({
    name: z.string().min(1).max(120),
    kind: z.enum(ROOM_KINDS).default('shared'),
    aiEnabled: z.boolean().default(true),
    memberIds: z.array(uuid).max(200).default([]),
});
export const PostMessageBody = z.object({
    text: z.string().min(1).max(20_000),
    clientMsgId: clientId,
    replyToId: uuid.optional(),
    attachmentIds: z.array(uuid).max(10).default([]),
    /**
     * "Devam" mode: AI providers the SENDER explicitly kept on in the composer for follow-up messages without
     * re-typing @handle. Same rules as a mention: the sender's own connection, budget and the room's AI switch.
     */
    continueWith: z
        .array(z.enum(PROVIDERS))
        .max(4)
        .default([])
        .transform((a) => [...new Set(a)]),
});
export const CreateConnectionBody = z.object({
    provider: z.enum(PROVIDERS),
    label: z.string().min(1).max(80),
    apiKey: z.string().min(8).max(512),
    defaultModel: z.string().min(1).max(120),
});
export const UpdateConnectionBody = z.object({
    defaultModel: z.string().min(1).max(120).optional(),
    label: z.string().min(1).max(80).optional(),
});
export const SetBudgetBody = z.object({
    provider: z.enum(PROVIDERS).nullable().default(null),
    projectId: uuid.nullable().default(null),
    softLimitUsd: z.number().min(0).max(100_000),
    hardLimitUsd: z.number().min(0).max(100_000),
});
export const WorkflowBody = z.object({
    clientMsgId: clientId,
    instruction: z.string().min(1).max(20_000),
    steps: z
        .array(z.object({ provider: z.enum(PROVIDERS), role: z.enum(['implement', 'review', 'fix', 'analyze']) }))
        .min(1)
        .max(5),
});
export const CreateTaskBody = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(10_000).default(''),
    roomId: uuid.optional(),
    repoId: uuid.optional(),
});
export const UpdateTaskBody = z.object({
    status: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']).optional(),
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(10_000).optional(),
});
export const CreateDecisionBody = z.object({
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(20_000),
    status: z.enum(['proposed', 'accepted']).default('proposed'),
    supersedesId: uuid.optional(),
    roomId: uuid.optional(),
});
export const AttachRepoBody = z.object({
    fullName: z.string().regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/),
    defaultBranch: z.string().min(1).max(100).default('main'),
});
export const ProfileEntryBody = z.object({
    key: z.string().min(1).max(80),
    value: z.string().min(1).max(4000),
    scope: z.enum(['private', 'ai_context']).default('private'),
});
export const CreateMcpTokenBody = z.object({
    label: z.string().min(1).max(80),
    projectIds: z.array(uuid).min(1).max(50),
    scopes: z.array(z.enum(['read', 'write_messages'])).min(1),
    ttlDays: z.number().int().min(1).max(90).default(30),
});
//# sourceMappingURL=api.js.map